// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Model,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';
import {
  RegattaRegistrationStatus,
  RegattaStatus,
} from 'common.data';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getImageUrl,
  scrollTop,
} from '@vroskus/library-helpers/dist/browser';
import {
  getFullItemUrl,
  getRegionByRegionSeasonId,
} from 'common.helpers/dist/browser';
import {
  getErrorLevel,
  setupParams,
} from '../../../helpers';
import config from '../../../config';

// Components
import Page from '../../layout/Page';
import Loader from '../../layout/Loader';
import BannersSection from '../../sections/RegattaPageSections/BannersSection';
// import AdvertisementsSection from '../../sections/RegattaPageSections/AdvertisementsSection';
import ContactsSection from '../../sections/RegattaPageSections/ContactsSection';
import DetailsSection from '../../sections/RegattaPageSections/DetailsSection';
import ChampionshipsSection from '../../sections/RegattaPageSections/ChampionshipsSection';
import DocumentsSection from '../../sections/RegattaPageSections/DocumentsSection';
import EntriesSection from '../../sections/RegattaPageSections/EntriesSection';
import MessagesSection from '../../sections/RegattaPageSections/MessagesSection';
import ProtestsSection from '../../sections/RegattaPageSections/ProtestsSection';
import RegistrationSection from '../../sections/RegattaPageSections/RegistrationSection';
import ResultsSection from '../../sections/RegattaPageSections/ResultsSection';
import ResultsOverallSection from '../../sections/RegattaPageSections/ResultsOverallSection';
import SchedulesSection from '../../sections/RegattaPageSections/SchedulesSection';
import SponsorsSection from '../../sections/RegattaPageSections/SponsorsSection';
import BoatRentsSection from '../../sections/RegattaPageSections/BoatRentsSection';
import TrackerSection from '../../sections/RegattaPageSections/TrackerSection';
// import AdvertisementFormSection from '../../sections/CommonSections/AdvertisementFormSection';
import DescriptionSection from '../../sections/CommonSections/DescriptionSection';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
  $SetRegionAction,
} from '../../../redux/modules/main/actions';
import type {
  $Tags,
} from '../../../types';

export type $OwnProps = $WithRouter;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
  setRegion: $SetRegionAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

const pageDataNotFoundErrorMessage = 'Page data not found';

type $State = {
  initialised: boolean;
  item: $Model<'Regatta'> | null;
  regattaEntryIdToEdit: null | string;
};

class RegattaPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      initialised: false,
      item: null,
      regattaEntryIdToEdit: null,
    };
  }

  componentDidMount(): void {
    this.init();
  }

  getTags(): $Tags {
    const {
      main,
    } = this.props;
    const {
      item,
    } = this.state;

    const tags: $Tags = {
      image: undefined,
      title: 'Regatta',
      url: undefined,
    };

    if (item !== null) {
      tags.title = item.Name;

      tags.url = getFullItemUrl({
        basePath: '/regatta',
        item,
        mainDomain: config.DOMAIN,
        regions: main.regions,
      });

      if (item.Image !== null) {
        tags.image = getImageUrl({
          cdnUrl: config.CDN_URL,
          image: item.Image,
          size: 'max',
        });
      }
    }

    return tags;
  }

  showRegistrationSection(): boolean {
    const {
      item,
    } = this.state;

    if (item !== null) {
      if (item.Status === RegattaStatus.ongoing
        && item.RegistrationStatus === RegattaRegistrationStatus.ended
      ) {
        return true;
      }

      return _.includes(
        [
          RegattaRegistrationStatus.upcoming,
          RegattaRegistrationStatus.ongoing,
          RegattaRegistrationStatus.off,
        ],
        item.RegistrationStatus,
      );
    }

    return false;
  }

  async init(): Promise<void> {
    const {
      main,
      params,
      redirect,
      setRegion,
    } = this.props;

    const {
      param1,
      param2,
    } = params;

    let fallbackParam: string = '';

    try {
      scrollTop();

      const preparedParams = setupParams(
        param1,
        param2,
      );

      if (preparedParams.param2) {
        fallbackParam = preparedParams.param1;
      }

      const item = await Services.Api.getRegattaPageData(preparedParams);

      if (item !== null) {
        const region = getRegionByRegionSeasonId({
          regions: main.regions,
          RegionSeasonId: item.RegionSeasonId,
        });

        await setRegion({
          RegionId: region.id,
        });

        this.setState({
          initialised: true,
          item,
        });
      } else {
        throw new Error('Page data not found');
      }
    } catch (error) {
      ErrorLog.exception(
        error as Error,
        getErrorLevel(
          error as Error,
          pageDataNotFoundErrorMessage,
        ),
      );

      redirect({
        pathname: `/${fallbackParam}`,
      });
    }
  }

  render() {
    const {
      initialised,
      item,
      regattaEntryIdToEdit,
    } = this.state;

    return (
      <Page
        id={webPages.regattaPage}
        tags={this.getTags()}
      >
        <Loader loading={!initialised}>
          {(item !== null ? (
            <>
              <DetailsSection item={item} />
              <BannersSection item={item} />
              {item.Content && (
                <DescriptionSection
                  data={item.Content}
                  id={webSections[webPages.regattaPage].description}
                />
              )}
              <SponsorsSection item={item} />
              <TrackerSection item={item} />
              <SchedulesSection item={item} />
              <BoatRentsSection item={item} />
              {this.showRegistrationSection() === true && (
                <RegistrationSection
                  item={item}
                  regattaEntryIdToEdit={regattaEntryIdToEdit}
                />
              )}
              <MessagesSection item={item} />
              <ProtestsSection item={item} />
              {_.includes(
                [
                  RegattaStatus.ongoing,
                  RegattaStatus.ended,
                ],
                item.Status,
              ) && (
                <>
                  <ResultsSection item={item} />
                  <ResultsOverallSection item={item} />
                </>
              )}
              <EntriesSection
                editRegattaEntry={({
                  RegattaEntryId,
                }) => this.setState({
                  regattaEntryIdToEdit: RegattaEntryId,
                })}
                item={item}
              />
              <DocumentsSection item={item} />
              <ContactsSection item={item} />
              <ChampionshipsSection item={item} />
              {
                /* <AdvertisementFormSection item={item} /> */
                /* <AdvertisementsSection item={item} /> */
              }
            </>
          ) : (
            <div />
          ))}
        </Loader>
      </Page>
    );
  }
}

export default RegattaPageView;
